import React, { useContext, useEffect, useState, useRef } from 'react';
import { createPortal }  from 'react-dom';
import { Rnd } from 'react-rnd';
import DashCard from './DashCard';
import DashStickyNote from './charts/sticky/DashStickyNote';
import './Dash.scss';
import { useCurrentUser } from '@src/hooks';
import { useCentralCalls } from '@src/rest/CentralCalls';
import { ProjectContext } from '../ProjectIndexPage';
import DashLabel from './charts/DashLabel';
import DashOrgChart from './charts/DashOrgChart';
import PptxGenJS from 'pptxgenjs';
import { createPowerPointSlide } from './charts/etc/PptUtils';
import { Icon, Button } from 'semantic-ui-react';
import { Flexbox, FlexItem, StyledContainer } from '@src/views/containers';
import { FlexJustify, FlexAlign, CorvalUIColor, Size } from '@src/types';
import BuildForm from './charts/builder/BuildForm';
import { DashScopeContext } from './DashScopeContext';

export const ChartTypeValues = [
    { display: 'Pie Chart', value: 'pie' },
    { display: 'Line Chart', value: 'line' },
    { display: 'Bar Chart', value: 'bar' },
    { display: 'Stacked Bar Chart', value: 'stackedBar' },
    { display: 'Single Value', value: 'singleValue' },
    { display: 'AI Model', value: 'aimodel' },
    { display: 'Filter List', value: 'filterList' },
    { display: 'Table', value: 'table' },
    { display: 'Org Chart', value: 'orgChart' },
    { display: 'Sticky Note', value: 'sticky' },
    { display: 'Label', value: 'label' },
    { display: 'Activity', value: 'activity' },
    { display: 'Tour', value: 'tour' },
    { display: 'Strategic View', value: 'strategicGanttView' },
    { display: 'PPT Gantt Summary', value: 'dashGanttView' },
    { display: 'Budget', value: 'budget' },
    { display: 'Nested Knowledge Report', value: 'nested' },
    { display: 'Executive Summary', value: 'summaryView' },
    { display: 'AI Content', value: 'aiContent' }
] as const;

export const IconTypeValues = [
    { display: 'Add', value: 'add' },
    { display: 'Alarm', value: 'alarm' },
    { display: 'Anchor', value: 'anchor' },
    { display: 'Archive', value: 'archive' },
    { display: 'Area Chart', value: 'area chart' },
    { display: 'Arrow Circle Down', value: 'arrow circle down' },
    { display: 'Arrow Circle Left', value: 'arrow circle left' },
    { display: 'Arrow Circle Right', value: 'arrow circle right' },
    { display: 'Arrow Circle Up', value: 'arrow circle up' },
    { display: 'Assistive Listening Systems', value: 'assistive listening systems' },
    { display: 'Asterisk', value: 'asterisk' },
    { display: 'At', value: 'at' },
    { display: 'Balance Scale', value: 'balance scale' },
    { display: 'Ban', value: 'ban' },
    { display: 'Barcode', value: 'barcode' },
    { display: 'Battery Full', value: 'battery full' },
    { display: 'Beer', value: 'beer' },
    { display: 'Bell', value: 'bell' },
    { display: 'Bicycle', value: 'bicycle' },
    { display: 'Binoculars', value: 'binoculars' },
    { display: 'Birthday Cake', value: 'birthday cake' },
    { display: 'Bomb', value: 'bomb' },
    { display: 'Book', value: 'book' },
    { display: 'Bookmark', value: 'bookmark' },
    { display: 'Briefcase', value: 'briefcase' },
    { display: 'Bug', value: 'bug' },
    { display: 'Bullhorn', value: 'bullhorn' },
    { display: 'Calculator', value: 'calculator' },
    { display: 'Calendar', value: 'calendar' },
    { display: 'Camera', value: 'camera' },
    { display: 'Car', value: 'car' },
    { display: 'Chart Bar', value: 'chart bar' },
    { display: 'Chart Line', value: 'chart line' },
    { display: 'Chart Pie', value: 'chart pie' },
    { display: 'Check Circle', value: 'check circle' },
    { display: 'Cloud', value: 'cloud' },
    { display: 'Coffee', value: 'coffee' },
    { display: 'Cogs', value: 'cogs' },
    { display: 'Compass', value: 'compass' },
    { display: 'Database', value: 'database' },
    { display: 'Download', value: 'download' },
    { display: 'Envelope', value: 'envelope' },
    { display: 'Eye', value: 'eye' },
    { display: 'Film', value: 'film' },
    { display: 'Flag', value: 'flag' },
    { display: 'Folder', value: 'folder' },
    { display: 'Gift', value: 'gift' },
    { display: 'Globe', value: 'globe' },
    { display: 'Heart', value: 'heart' },
    { display: 'Home', value: 'home' },
] as const;

export const DashGridItemFields = [
    // Positional fields are managed by drag/resize — hidden from the config panel
    { idName: 'coordinateX', displayName: 'Coordinate X', controlType: 'number', size: 'half', hide: true },
    { idName: 'coordinateY', displayName: 'Coordinate Y', controlType: 'number', size: 'half', hide: true },
    { idName: 'height', displayName: 'Height', controlType: 'number', size: 'half', hide: true },
    { idName: 'width', displayName: 'Width', controlType: 'number', size: 'half', hide: true },
    { idName: 'title', displayName: 'Title', controlType: 'text' },
    { idName: 'subtitle', displayName: 'Subtitle', controlType: 'text' },
    { idName: 'procedureName', displayName: 'Procedure Name', controlType: 'procedures' },
    { idName: 'chartType', displayName: 'Chart Type', controlType: 'enum' },
    { idName: 'style', displayName: 'Style', controlType: 'text', hide: true },
    { idName: 'showHeaderFooter', displayName: 'Show Header/Footer', controlType: 'boolean' },
    { idName: 'pivotColumn', displayName: 'Pivot Column', controlType: 'text', hide: true },
    { idName: 'pivotDataColumn', displayName: 'Pivot Data Column', controlType: 'text', hide: true },
    { idName: 'pivotIdColumn', displayName: 'Pivot ID Column', controlType: 'text', hide: true },
    { idName: 'canEdit', displayName: 'Can Edit', controlType: 'boolean', hide: true },
    { idName: 'editColumn', displayName: 'Edit Column', controlType: 'text', hide: true },
    { idName: 'procedureId', displayName: 'Procedure ID', controlType: 'text', hide: true, disable: true },
    { idName: 'paramName', displayName: 'Parameter Name', controlType: 'text' },
    { idName: 'filterListDisplayColumnName', displayName: 'Filter List Display Column Name', controlType: 'text' },
    { idName: 'iconName', displayName: 'Icon', controlType: 'icon' },
    { idName: 'ignoreFilter', displayName: 'Ignore Filters', controlType: 'boolean' },
    { idName: 'tableName', displayName: 'Filter Table Name', controlType: 'text' },
    { idName: 'aiContext', displayName: 'AI Context', controlType: 'text' },
] as const;

export type ChartTypes = typeof ChartTypeValues[number]['value'];
export type ChartTypeOption = typeof ChartTypeValues[number];
export type IconOptions = typeof IconTypeValues[number]['value'];

// Widgets that only need their own procedure's data, so they also work outside a project.
const SCOPE_INDEPENDENT_CHART_TYPES: ReadonlyArray<ChartTypes> = [
    'pie', 'line', 'bar', 'stackedBar', 'singleValue', 'table', 'filterList', 'label', 'aiContent',
];

export interface DashGridItem {
    coordinateX: number;
    coordinateY: number;
    height: number;
    width: number;
    title: string;
    subtitle: string;
    procedureName: string;
    chartType?: ChartTypes;
    style?: string;
    showHeaderFooter?: boolean;
    pivotColumn?: string;
    pivotDataColumn?: string;
    pivotIdColumn?: string;
    canEdit?: IconOptions;
    editColumn?: string;
    procedureId?: string;
    paramName?: string;
    filterListDisplayColumnName?: string;
    iconName?: string;
    ignoreFilter: boolean;
    tableName: string;
    nestedReportId: number;
}

interface DashGridProps {
    pageJson: DashGridItem[];
    editMode: boolean;
    dashPageId: string;
    /** Called when the grid wants to exit edit mode (Save or Discard was clicked) */
    onExitEditMode?: () => void;
    /** Called with the layout whenever it is written to the DashPage row, so the host can keep its copy in sync */
    onLayoutSaved?: (pageJson: DashGridItem[]) => void;
}

interface EditHistoryState { snapshots: DashGridItem[][]; idx: number; }
type EditHistoryAction =
    | { type: 'init'; snapshot: DashGridItem[] }
    | { type: 'push'; snapshot: DashGridItem[] }
    | { type: 'undo' }
    | { type: 'redo' };

function editHistoryReducer(s: EditHistoryState, a: EditHistoryAction): EditHistoryState {
    switch (a.type) {
        case 'init': return { snapshots: [a.snapshot], idx: 0 };
        case 'push': {
            const snaps = s.snapshots.slice(0, s.idx + 1);
            return { snapshots: [...snaps, a.snapshot], idx: s.idx + 1 };
        }
        case 'undo': return s.idx > 0 ? { ...s, idx: s.idx - 1 } : s;
        case 'redo': return s.idx < s.snapshots.length - 1 ? { ...s, idx: s.idx + 1 } : s;
        default:     return s;
    }
}

const ROW_HEIGHT = 220; // px per grid row unit (including gap)
const COL_COUNT = 6;

const useContainerWidth = (ref: React.RefObject<HTMLDivElement>) => {
    const [width, setWidth] = useState(0);
    useEffect(() => {
        if (!ref.current) return;
        // ResizeObserver fires on ANY container width change (window resize, sidebar
        // toggle, panel open/close) — more accurate than window 'resize' events.
        const observer = new ResizeObserver(entries => {
            setWidth(entries[0]?.contentRect.width || 0);
        });
        observer.observe(ref.current);
        setWidth(ref.current.offsetWidth);
        return () => observer.disconnect();
    }, [ref]);
    return width;
};

interface DragGuide {
    type: 'horizontal' | 'vertical';
    position: number;
    danger: boolean;
}

interface LiveRect { x: number; y: number; width: number; height: number; }
// The rendered insertion-line indicator. `horizontal` drives the CSS class and
// the dot-cap orientation (left/right vs top/bottom).
interface InsertionLineRect extends LiveRect { horizontal: boolean; }

const DashGrid: React.FC<DashGridProps> = ({ pageJson, editMode, dashPageId, onExitEditMode, onLayoutSaved }) => {
    const [ dashJson, setDashJson ]                     = useState<DashGridItem[]>(pageJson || []),
          [ filterParams, setFilterParams ]             = useState<any>(),
          [ slideData, setSlideData ]                   = useState<{ item: DashGridItem; chartRef?: HTMLDivElement | null; realChartData?: any[] }[]>( [] ),
          [ editingItem, setEditingItem ]               = useState<{ item: DashGridItem; index: number } | null>( null ),
          [ procedures, setProcedures ]                 = useState<any[]>( [] ),
          [ deleteConfirmIndex, setDeleteConfirmIndex ] = useState<number | null>( null ),
          [ guides, setGuides ]                         = useState<DragGuide[]>( [] );
    // Per-card bump counter: incrementing forces a remount, snapping the card back
    // to its last saved position. Only fired on genuine collision rejection.
    const [cardBumpKeys, setCardBumpKeys] = useState<Record<number, number>>({});
    const bumpCardKey = (index: number) =>
        setCardBumpKeys(prev => ({ ...prev, [index]: (prev[index] || 0) + 1 }));

    // Global version counter: incrementing forces ALL Rnd components to remount,
    // making them re-read their `default` position from the restored dashJson.
    // Used on undo, redo, and discard so card positions visually snap back.
    const [undoRedoKey, setUndoRedoKey] = useState(0);

    // Insertion-line indicator shown during drag in edit mode.
    // A thin vertical (or horizontal) line drawn on the edge of the target card
    // to show exactly where in the sequence the drag card will be inserted.
    const [insertionLine, setInsertionLine] = useState<InsertionLineRect | null>(null);

    const [editHistory, dispatchHistory] = React.useReducer(
        editHistoryReducer,
        { snapshots: [], idx: 0 }
    );
    const canUndo = editHistory.idx > 0;
    const canRedo = editHistory.idx < editHistory.snapshots.length - 1;
    const isDirty = editHistory.idx > 0;

    // Refs for tracking editMode transitions and preventing double-commits
    const prevEditModeRef = useRef( false ),
          exitHandledRef  = useRef( false ),
          dashJsonRef     = useRef(dashJson);
    dashJsonRef.current   = dashJson;

    const { projectId, projectVersionId } = useContext(ProjectContext);
    const { scope } = useContext(DashScopeContext);
    const chartTypeOptions: ReadonlyArray<ChartTypeOption> = scope === 'project'
        ? ChartTypeValues
        : ChartTypeValues.filter(type => SCOPE_INDEPENDENT_CHART_TYPES.includes(type.value));
    const { currentUser } = useCurrentUser();
    const { RunUpdate, TableSelect } = useCentralCalls();
    const containerRef = useRef<HTMLDivElement>( null ),
          wrapperRef   = useRef<HTMLDivElement>( null );
    const containerWidth = useContainerWidth(containerRef);

    // Source-of-truth pixel rects for collision detection.
    // Tracking these separately from dashJson prevents the drift that occurs in
    // uncontrolled Rnd mode, where visual positions diverge from grid coordinates
    // after a few moves, causing false collision positives on genuinely empty space.
    const cardRectsRef    = useRef<LiveRect[]>( [] ),
          rectsInitKeyRef = useRef<string>( '' );

    // Note: dashJson is intentionally NOT synced from pageJson after mount.
    // Page switches are handled by the key={`dash-grid-${dashPageId}`} prop in
    // DashContainerPage, which remounts this component and re-initialises via
    // useState(pageJson || []).  Syncing on every pageJson reference change would
    // reset dashJson whenever DashContainerPage re-renders (e.g. toggling editMode),
    // which destroys any widgets added or configured during the current edit session.

    // Fetch procedures list when entering edit mode
    useEffect(() => {
        if (!editMode) { setEditingItem(null); return; }
        TableSelect('DashProcedure', { Scope: scope })
            .then(json => setProcedures(json || []))
            .catch(console.error);
    }, [editMode, scope]);

    // Track edit mode transitions:
    //  - entering → snapshot current state as the baseline for this session
    //  - exiting via ✓ sidebar button → auto-commit (Save & Exit path sets
    //    exitHandledRef so this is skipped when we triggered the exit ourselves)
    useEffect(() => {
        const wasEditing = prevEditModeRef.current;
        prevEditModeRef.current = editMode;

        if (!wasEditing && editMode) {
            // Initialise history with the current canvas state
            dispatchHistory({ type: 'init', snapshot: dashJsonRef.current });
        }

        if (wasEditing && !editMode) {
            if (!exitHandledRef.current) {
                // Exited via the ✓ button (external) — auto-save any pending changes
                commitToDb(dashJsonRef.current);
            }
            exitHandledRef.current = false;
        }
    }, [editMode]); // eslint-disable-line react-hooks/exhaustive-deps


    const handleUndo = () => {
        if (!canUndo) return;
        const prevSnapshot = editHistory.snapshots[editHistory.idx - 1];
        setDashJson(prevSnapshot);
        dispatchHistory({ type: 'undo' });
        // Reset cardRectsRef and bump the global key so every Rnd remounts
        // from the restored dashJson — uncontrolled Rnd ignores `default` changes
        // after mount, so a remount is the only reliable way to reposition them.
        rectsInitKeyRef.current = '';
        setCardBumpKeys({});
        setUndoRedoKey(k => k + 1);
    };

    const handleRedo = () => {
        if (!canRedo) return;
        const nextSnapshot = editHistory.snapshots[editHistory.idx + 1];
        setDashJson(nextSnapshot);
        dispatchHistory({ type: 'redo' });
        rectsInitKeyRef.current = '';
        setCardBumpKeys({});
        setUndoRedoKey(k => k + 1);
    };


    const handleSave = () => {
        exitHandledRef.current = true; // prevent the useEffect from double-committing
        commitToDb(dashJsonRef.current);
        setEditingItem(null);
        onExitEditMode?.();
    };

    const handleDiscard = () => {
        exitHandledRef.current = true;
        const original = editHistory.snapshots[0];
        if (original) {
            setDashJson(original);
            rectsInitKeyRef.current = '';
            setCardBumpKeys({});
            setUndoRedoKey(k => k + 1);
        }
        setEditingItem(null);
        onExitEditMode?.();
    };


    // Write to DB (the "real" commit).  All in-edit actions only call saveDashJson
    // (local state + history) — commitToDb is called explicitly on Save or auto-save.
    const commitToDb = (json: DashGridItem[]) => {
        RunUpdate('DashPage', dashPageId, { PageConfig: JSON.stringify(json) });
        onLayoutSaved?.(json);
    };

    // Local-only save: updates React state and pushes onto the undo/redo history.
    // DB is NOT written until the user explicitly clicks Save (or auto-save on ✓).
    const saveDashJson = (updated: DashGridItem[]) => {
        setDashJson(updated);
        dispatchHistory({ type: 'push', snapshot: updated });
    };


    const colWidth = containerWidth > 0 ? containerWidth / COL_COUNT : 0;

    // Debounced version of colWidth — only updates 200 ms after resizing stops.
    // Included in every Rnd key so cards remount (and re-read `default` positions)
    // once the window/container has settled, without remounting on every pixel.
    const [ stableColWidth, setStableColWidth ] = useState(0),
          resizeTimerRef                        = useRef<any>( null );
    useEffect(() => {
        if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = setTimeout(() => {
            setStableColWidth(Math.round(colWidth));
        }, 200);
        return () => { if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current); };
    }, [colWidth]);

    // Canvas must always be tall enough to contain every card plus breathing room.
    // This drives the scrollable height — .dashboard-content handles the scrollbar.
    const canvasHeight = Math.max(
        600,
        ...dashJson.map(item => (item.coordinateY - 1 + item.height) * ROW_HEIGHT + ROW_HEIGHT)
    );

    // (Re)initialise cardRectsRef whenever the page or container width changes so
    // we start from a clean, consistent set of positions derived from saved data.
    const rectsInitKey = `${dashPageId}-${Math.round(colWidth)}`;
    if (colWidth > 0 && rectsInitKeyRef.current !== rectsInitKey) {
        rectsInitKeyRef.current = rectsInitKey;
        cardRectsRef.current = dashJson.map(item => ({
            x: (item.coordinateX - 1) * colWidth,
            y: (item.coordinateY - 1) * ROW_HEIGHT,
            width: item.width * colWidth,
            height: item.height * ROW_HEIGHT - 20,
        }));
    }

    const toPixels = (item: DashGridItem) => ({
        x: (item.coordinateX - 1) * colWidth,
        y: (item.coordinateY - 1) * ROW_HEIGHT,
        width: item.width * colWidth,
        height: item.height * ROW_HEIGHT - 20,
    });

    const toGridUnits = (px: { x: number; y: number; width: number; height: number }) => ({
        coordinateX: Math.max(1, Math.round(px.x / colWidth) + 1),
        coordinateY: Math.max(1, Math.round(px.y / ROW_HEIGHT) + 1),
        width: Math.max(1, Math.min(COL_COUNT, Math.round(px.width / colWidth))),
        height: Math.max(1, Math.round((px.height + 20) / ROW_HEIGHT)),
    });

    // Uses cardRectsRef (actual visual positions) rather than toPixels(dashJson)
    // to avoid drift between grid-saved coordinates and where cards visually are.
    // 2px inward shrink so touching cards (shared edges) never count as collisions.

    const hasCollisionPx = (dragIndex: number, rect: LiveRect): boolean => {
        const al = rect.x + 2;
        const ar = rect.x + rect.width - 2;
        const at = rect.y + 2;
        const ab = rect.y + rect.height - 2;

        return cardRectsRef.current.some((o, i) => {
            if (i === dragIndex || !o) return false;
            return al < o.x + o.width && ar > o.x && at < o.y + o.height && ab > o.y;
        });
    };


    const updateGuides = (
        dragIndex: number,
        rect: LiveRect,
        opts?: { showDanger?: boolean; hideWhenColliding?: boolean }
    ) => {
        const showDanger = opts?.showDanger ?? true;
        const hideWhenColliding = opts?.hideWhenColliding ?? false;

        // In drag/shift mode, overlap is expected (that's how insertion works).
        // Hide proximity guides entirely so they don't imply "cannot place here".
        if (hideWhenColliding && hasCollisionPx(dragIndex, rect)) {
            setGuides([]);
            return;
        }

        // Proximity threshold: 8% of a column width, min 10px.
        // Keeps guide lines from going red unless genuinely close to a neighbour.
        const threshold = Math.max(10, colWidth * 0.08);

        const edges = {
            top:    rect.y,
            bottom: rect.y + rect.height,
            left:   rect.x,
            right:  rect.x + rect.width,
        };

        let topNear = false, bottomNear = false, leftNear = false, rightNear = false;
        let topDanger = false, bottomDanger = false, leftDanger = false, rightDanger = false;

        cardRectsRef.current.forEach((o, i) => {
            if (i === dragIndex || !o) return;
            const oe = { top: o.y, bottom: o.y + o.height, left: o.x, right: o.x + o.width };

            // Only flag the edge that is actually approaching another card's opposing edge,
            // AND only when the perpendicular ranges overlap (so we don't flag when merely
            // row-aligned but far apart horizontally, for example).
            const hOverlap = edges.left < oe.right && edges.right > oe.left;
            const vOverlap = edges.top  < oe.bottom && edges.bottom > oe.top;

            if (hOverlap && Math.abs(edges.bottom - oe.top)   < threshold) bottomNear = true;
            if (hOverlap && Math.abs(edges.top    - oe.bottom) < threshold) topNear    = true;
            if (vOverlap && Math.abs(edges.right  - oe.left)   < threshold) rightNear  = true;
            if (vOverlap && Math.abs(edges.left   - oe.right)  < threshold) leftNear   = true;

            // Actual overlap — all four edges go red
            if (showDanger && hOverlap && vOverlap &&
                edges.left + 4 < oe.right && edges.right - 4 > oe.left &&
                edges.top  + 4 < oe.bottom && edges.bottom - 4 > oe.top) {
                topNear = bottomNear = leftNear = rightNear = true;
                topDanger = bottomDanger = leftDanger = rightDanger = true;
            }
        });

        const nextGuides: DragGuide[] = [];
        if (topNear)    nextGuides.push({ type: 'horizontal', position: edges.top,    danger: topDanger });
        if (bottomNear) nextGuides.push({ type: 'horizontal', position: edges.bottom, danger: bottomDanger });
        if (leftNear)   nextGuides.push({ type: 'vertical',   position: edges.left,   danger: leftDanger });
        if (rightNear)  nextGuides.push({ type: 'vertical',   position: edges.right,  danger: rightDanger });
        setGuides(nextGuides);
    };

    const clearGuides = () => setGuides([]);



    // Pack an ordered list of cards into the grid exactly like CSS flex-wrap:
    // fill left-to-right; when a card doesn't fit on the current row, wrap it to
    // the start of the next row.  Row height = max card height on that row.
    const packCards = (items: DashGridItem[]): DashGridItem[] => {
        const out: DashGridItem[] = [];
        let x = 1, y = 1, rowH = 0;
        for (const item of items) {
            if (x + item.width - 1 > COL_COUNT) {
                y  += rowH;
                x   = 1;
                rowH = 0;
            }
            out.push({ ...item, coordinateX: x, coordinateY: y });
            x    += item.width;
            rowH  = Math.max(rowH, item.height);
        }
        return out;
    };

    // Decide insertion side for a specific target card.
    // Axis selection still uses normalized center offsets (supports 4-direction
    // indicators), but before/after uses the dragged card's leading edge on that
    // axis. This allows "insert before left-most card" when x is clamped at 0.
    const getInsertDecision = (
        targetRect: LiveRect,
        dropPx: LiveRect
    ): { insertAfter: boolean; isHorizontal: boolean } => {
        const dropCx = dropPx.x + dropPx.width  / 2;
        const dropCy = dropPx.y + dropPx.height / 2;
        const midX   = targetRect.x + targetRect.width  / 2;
        const midY   = targetRect.y + targetRect.height / 2;

        const normX = Math.abs(dropCx - midX) / Math.max(1, targetRect.width  / 2);
        const normY = Math.abs(dropCy - midY) / Math.max(1, targetRect.height / 2);
        const isHorizontal = normY > normX; // vertical drag intent => horizontal insert line

        // Leading-edge anchor (not center) fixes unreachable "before left-most".
        const anchor = isHorizontal ? dropPx.y : dropPx.x;
        const threshold = isHorizontal ? midY : midX;
        return { insertAfter: anchor >= threshold, isHorizontal };
    };

    // Reorder the grid as a flex-wrapped sequence after a shift-drop.
    //
    // Algorithm:
    //  1. Sort all OTHER cards in their current reading order (top→bottom, left→right).
    //  2. Find which card the drop position overlaps.
    //  3. Determine insert-before vs insert-after from the drop centre relative to
    //     the target card's horizontal midpoint.
    //  4. Reinsert the drag card at that sequence position.
    //  5. Pack the new sequence with packCards → flex-wrap behaviour.
    //
    // Example: grid=3w, A(1w) B(1w) C(1w) fill row 1.  Drag D(2w) between A and B:
    //   sequence becomes [A, D, B, C] → pack → A(1,1) D(2,1) | B(1,2) C(2,2)
    //   A stays, D lands beside A, B & C wrap to row 2 exactly as flex-wrap would.
    const buildFlexReflow = (dragIndex: number, dropPx: LiveRect): DashGridItem[] => {
        const drag   = dashJson[dragIndex];
        const dropCx = dropPx.x + dropPx.width  / 2;

        // Other cards in reading order
        const others = Array.from({ length: dashJson.length }, (_, i) => i)
            .filter(i => i !== dragIndex)
            .sort((a, b) => {
                const dy = dashJson[a].coordinateY - dashJson[b].coordinateY;
                return dy !== 0 ? dy : dashJson[a].coordinateX - dashJson[b].coordinateX;
            });

        if (others.length === 0) {
            const gu     = toGridUnits(dropPx);
            const result = dashJson.map(item => ({ ...item }));
            result[dragIndex] = { ...drag, coordinateX: gu.coordinateX, coordinateY: gu.coordinateY };
            return result;
        }

        // Find best overlap target only. If none overlap, keep free-drag placement.
        let targetIdx = -1;
        let bestArea  = 0;
        cardRectsRef.current.forEach((o, i) => {
            if (i === dragIndex || !o) return;
            const xOv = Math.max(0, Math.min(dropPx.x + dropPx.width,  o.x + o.width)  - Math.max(dropPx.x, o.x));
            const yOv = Math.max(0, Math.min(dropPx.y + dropPx.height, o.y + o.height) - Math.max(dropPx.y, o.y));
            const area = xOv * yOv;
            if (area > bestArea) { bestArea = area; targetIdx = i; }
        });

        if (targetIdx < 0 || bestArea <= 0) {
            const gu     = toGridUnits(dropPx);
            const result = dashJson.map(item => ({ ...item }));
            result[dragIndex] = { ...drag, coordinateX: gu.coordinateX, coordinateY: gu.coordinateY };
            return result;
        }

        // Determine insert position using the exact same decision helper used by
        // getInsertionPoint so the line and the final reflow always agree.
        const tRect  = targetIdx >= 0 ? cardRectsRef.current[targetIdx] : null;
        let insertAfter = true;
        if (tRect) {
            insertAfter = getInsertDecision(tRect, dropPx).insertAfter;
        }
        const targetSeq   = others.indexOf(targetIdx);
        const insertAt    = Math.max(0, insertAfter ? targetSeq + 1 : targetSeq);

        const newOrder = [
            ...others.slice(0, insertAt),
            dragIndex,
            ...others.slice(insertAt),
        ];

        // Pack and map back to result array
        const packed = packCards(newOrder.map(i => dashJson[i]));
        const result = dashJson.map(item => ({ ...item }));
        newOrder.forEach((origIdx, seqPos) => {
            result[origIdx] = {
                ...result[origIdx],
                coordinateX: packed[seqPos].coordinateX,
                coordinateY: packed[seqPos].coordinateY,
            };
        });
        return result;
    };

    // Returns the target card index, whether we'll insert after it, and which axis
    // dominates (horizontal line = top/bottom drag, vertical line = left/right drag).
    //
    // Dominant axis is determined by the normalised offset from the drop centre to
    // the target card centre: whichever dimension is proportionally larger wins.
    // This means a tall card dragged above a wide card still gets the right line.
    const getInsertionPoint = (
        dragIndex: number,
        dropPx: LiveRect
    ): { targetIdx: number; insertAfter: boolean; isHorizontal: boolean } => {
        const dropCx = dropPx.x + dropPx.width  / 2;
        const dropCy = dropPx.y + dropPx.height / 2;

        let targetIdx = -1;
        let bestArea  = 0;
        cardRectsRef.current.forEach((o, i) => {
            if (i === dragIndex || !o) return;
            const xOv = Math.max(0, Math.min(dropPx.x + dropPx.width,  o.x + o.width)  - Math.max(dropPx.x, o.x));
            const yOv = Math.max(0, Math.min(dropPx.y + dropPx.height, o.y + o.height) - Math.max(dropPx.y, o.y));
            const area = xOv * yOv;
            if (area > bestArea) { bestArea = area; targetIdx = i; }
        });
        if (targetIdx < 0 || bestArea <= 0) return { targetIdx: -1, insertAfter: false, isHorizontal: false };

        const tRect = targetIdx >= 0 ? cardRectsRef.current[targetIdx] : null;
        if (!tRect) return { targetIdx, insertAfter: false, isHorizontal: false };

        const { insertAfter, isHorizontal } = getInsertDecision(tRect, dropPx);
        return { targetIdx, insertAfter, isHorizontal };
    };

    const handleDragStop = (index: number, d: { x: number; y: number }) => {
        clearGuides();
        setInsertionLine(null);

        const current = dashJson[index];
        const pxRect: LiveRect = {
            x: d.x,
            y: d.y,
            width: current.width * colWidth,
            height: current.height * ROW_HEIGHT - 20,
        };

        // Click/no-op guard: don't persist or reorder when the card didn't move.
        const prevRect = cardRectsRef.current[index] || toPixels(current);
        const moved = Math.abs(pxRect.x - prevRect.x) > 2 || Math.abs(pxRect.y - prevRect.y) > 2;
        if (!moved) return;

        // Free drag wins when there is enough open space at the drop location.
        if (!hasCollisionPx(index, pxRect)) {
            const gridUnits = toGridUnits(pxRect);
            const changed = gridUnits.coordinateX !== current.coordinateX || gridUnits.coordinateY !== current.coordinateY;
            if (!changed) return;
            const updated = dashJson.map((item, i) =>
                i === index ? { ...item, coordinateX: gridUnits.coordinateX, coordinateY: gridUnits.coordinateY } : item
            );
            cardRectsRef.current = cardRectsRef.current.map((r, i) => i === index ? pxRect : r);
            saveDashJson(updated);
            return;
        }

        // Shift mode: only when dropped on top of another card.
        // Reinsert at target and flex-wrap pack.
        const updated = buildFlexReflow(index, pxRect);
        saveDashJson(updated);
        // Rebuild every card's pixel rect from the authoritative grid coords and
        // remount all Rnd components so they animate to their new positions.
        cardRectsRef.current = updated.map(item => toPixels(item));
        setCardBumpKeys({});
        setUndoRedoKey(k => k + 1);
    };

    const handleResizeStop = (
        index: number,
        ref: HTMLElement,
        position: { x: number; y: number }
    ) => {
        clearGuides();
        setInsertionLine(null);
        const pxRect: LiveRect = {
            x: position.x,
            y: position.y,
            width: ref.offsetWidth,
            height: ref.offsetHeight,
        };
        if (hasCollisionPx(index, pxRect)) {
            bumpCardKey(index); // remount card → snaps back to last known good size
            return;
        }
        // Record the accepted visual position so future collision checks are accurate
        cardRectsRef.current = cardRectsRef.current.map((r, i) => i === index ? pxRect : r);
        const gridUnits = toGridUnits(pxRect);
        const updated = dashJson.map((item, i) =>
            i === index ? { ...item, ...gridUnits } : item
        );
        saveDashJson(updated);
    };


    const handleDeleteItem = (index: number) => {
        const updated = dashJson.filter((_, i) => i !== index);
        saveDashJson(updated);
        if (editingItem?.index === index) setEditingItem(null);
        setDeleteConfirmIndex(null);
    };

    // Scan the grid row-by-row, column-by-column for the first open slot that fits
    // the requested dimensions. Falls back to a new row below all existing cards.
    const findFirstAvailablePosition = (
        items: DashGridItem[],
        w: number,
        h: number
    ): { coordinateX: number; coordinateY: number } => {
        const maxRow = items.reduce((max, item) => Math.max(max, item.coordinateY + item.height - 1), 0);

        for (let row = 1; row <= maxRow + h; row++) {
            for (let col = 1; col <= COL_COUNT - w + 1; col++) {
                const fits = !items.some(item => {
                    const aL = col,           aR = col + w;
                    const aT = row,           aB = row + h;
                    const bL = item.coordinateX, bR = item.coordinateX + item.width;
                    const bT = item.coordinateY, bB = item.coordinateY + item.height;
                    return aL < bR && aR > bL && aT < bB && aB > bT;
                });
                if (fits) return { coordinateX: col, coordinateY: row };
            }
        }

        // Fallback: place below all existing cards
        return { coordinateX: 1, coordinateY: maxRow + 1 };
    };

    const handleAddWidget = () => {
        const w = 2, h = 2;
        // Always read from the latest committed in-memory layout snapshot.
        // This avoids stale placement when users add widgets after drag/reflow cycles.
        const currentItems = dashJsonRef.current || [];
        const { coordinateX, coordinateY } = findFirstAvailablePosition(currentItems, w, h);
        const newItem: DashGridItem = {
            coordinateX,
            coordinateY,
            width: w,
            height: h,
            title: 'New Widget',
            subtitle: '',
            procedureName: '',
            ignoreFilter: false,
            tableName: '',
            nestedReportId: null,
        };
        const updated = [...currentItems, newItem];
        // Keep local edit state + undo/redo history in sync for add-widget actions.
        saveDashJson(updated);
        // Rebuild full rect map from authoritative grid coordinates (safer than append).
        cardRectsRef.current = updated.map(item => toPixels(item));
        RunUpdate('DashPage', dashPageId, { PageConfig: JSON.stringify(updated) });
        setEditingItem({ item: newItem, index: updated.length - 1 });
    };

    const handleItemFormSave = (updatedFields: Partial<DashGridItem>) => {
        if (editingItem === null) return;
        const merged = { ...editingItem.item, ...updatedFields };
        const updated = dashJson.map((item, i) => i === editingItem.index ? merged : item);
        saveDashJson(updated);
        setEditingItem(null);
    };


    const setParameterChange = (data: any) => {
        if (!data || typeof data !== 'object') return;
        setFilterParams(prev => ({ ...prev, ...data }));
    };

    const handleSlideReady = (data: { item: DashGridItem; chartRef?: HTMLDivElement | null; realChartData?: any[] }) => {
        setSlideData(prev => [...prev, data]);
    };

    const exportAllToPPT = async () => {
        const pptx = new PptxGenJS();
        for (const card of dashJson) {
            if (!['table', 'pie', 'bar', 'stackedBar'].includes(card.chartType)) continue;
            handleSlideReady({ item: card, realChartData: [], chartRef: null });
        }
        for (const { item, chartRef, realChartData } of slideData) {
            await createPowerPointSlide(pptx, item, chartRef, realChartData);
        }
        pptx.writeFile({ fileName: 'Corval_Dashboard.pptx' });
    };


    const renderCardContent = (item: DashGridItem, index: number) => {
        if (item.style === 'sticky') {
            return <DashStickyNote title={item.title} content={item.subtitle} />;
        }
        if (item.chartType === 'orgChart') {
            return <DashOrgChart item={item} projectVersionId={projectVersionId} userId={currentUser.id} />;
        }
        if (item.chartType === 'label') {
            return <DashLabel item={item} projectVersionId={projectVersionId} userId={currentUser.id} />;
        }
        return (
            <DashCard
                item={item}
                projectVersionId={projectVersionId}
                projectId={projectId}
                userId={currentUser.id}
                editMode={false}
                setFilterParams={setParameterChange}
                filterParams={filterParams}
                onSlideReady={handleSlideReady}
            />
        );
    };

    if (!pageJson) return null;

    const isEditPanelOpen = editMode && editingItem !== null;

    // Measure the dashboard wrapper's position in the viewport so the portal
    // panel appears to float *within* the dashboard area (below the top navbar
    // and above the page footer) rather than covering the full screen.
    const getPanelBounds = (): { top: number; bottom: number } => {
        // Use the navbar bottom as the panel's top anchor so the panel always
        // starts below the nav regardless of scroll position.
        // wrapperTop goes negative when the user has scrolled down — clamping to
        // the navbar bottom prevents the panel header from disappearing above the
        // viewport (which caused the "blank panel" bug on bottom widgets).
        const wrapperTop = wrapperRef.current?.getBoundingClientRect().top ?? 0;
        // Find the page header / top nav so we never clip under it.
        const navEl  = document.querySelector('.corval-nav, nav, header, [class*="navbar"], [class*="top-bar"]');
        const navBottom = navEl ? navEl.getBoundingClientRect().bottom : 0;
        const clampedTop = Math.max(navBottom, wrapperTop);

        const footerEl  = document.querySelector('.layout--frame-legal');
        const footerTop = footerEl ? footerEl.getBoundingClientRect().top : window.innerHeight;
        const bottomGap = Math.max(0, window.innerHeight - footerTop);
        return { top: Math.round(clampedTop), bottom: Math.round(bottomGap) };
    };

    // Determine which side the panel should open from so it never covers the
    // widget being edited.  We look at where the widget's center sits in the
    // viewport: if it's in the right half, slide the panel in from the LEFT.
    const getPanelSide = (): 'left' | 'right' => {
        if (!editingItem || !containerRef.current) return 'right';
        const containerRect = containerRef.current.getBoundingClientRect();
        const widgetRect    = cardRectsRef.current[editingItem.index];
        if (!widgetRect) return 'right';
        const widgetCenterVX = containerRect.left + widgetRect.x + widgetRect.width / 2;
        return widgetCenterVX > window.innerWidth / 2 ? 'left' : 'right';
    };

    return (
        <div ref={wrapperRef} className={`dash-grid-wrapper ${editMode ? 'edit-mode-active' : ''}`}>
            {editMode && (
                <div className="dash-edit-toolbar">
                    {/* Left group: add + undo/redo */}
                    <div className="dash-edit-toolbar-left">
                        <button className="dash-edit-add-btn" onClick={handleAddWidget}>
                            <Icon name="plus" />
                            Add Widget
                        </button>
                        <button
                            className="dash-edit-history-btn"
                            onClick={handleUndo}
                            disabled={!canUndo}
                            title="Undo"
                        >
                            <Icon name="undo" />
                        </button>
                        <button
                            className="dash-edit-history-btn"
                            onClick={handleRedo}
                            disabled={!canRedo}
                            title="Redo"
                        >
                            <Icon name="redo" />
                        </button>
                    </div>

                    {/* Centre: hint */}
                    <span className="dash-edit-hint">
                        <Icon name="arrows alternate" /> Drag the top bar to move &nbsp;·&nbsp;
                        <Icon name="expand arrows alternate" /> Drag corners to resize &nbsp;·&nbsp;
                        <Icon name="cog" /> Click to configure
                    </span>

                    {/* Right group: discard + save */}
                    <div className="dash-edit-toolbar-right">
                        <button
                            className="dash-edit-discard-btn"
                            onClick={handleDiscard}
                        >
                            <Icon name="times" />
                            {isDirty ? 'Discard Changes' : 'Exit'}
                        </button>
                        <button
                            className="dash-edit-save-btn"
                            onClick={handleSave}
                        >
                            <Icon name="check" />
                            Save
                        </button>
                    </div>
                </div>
            )}
            <div
                className="dash-grid-container"
                ref={containerRef}
                style={{
                    position: 'relative',
                    width: '100%',
                    height: canvasHeight,
                }}
            >
                {editMode && insertionLine && (
                    <div
                        className={`dash-insertion-line${insertionLine.horizontal ? ' horizontal' : ''}`}
                        style={{
                            position: 'absolute',
                            pointerEvents: 'none',
                            zIndex: 1001,
                            left:   insertionLine.x,
                            top:    insertionLine.y,
                            width:  insertionLine.width,
                            height: insertionLine.height,
                        }}
                    />
                )}
                {guides.map((guide, i) => (
                    <div
                        key={`guide-${i}`}
                        className={`dash-guide-line ${guide.danger ? 'danger' : ''}`}
                        style={{
                            position: 'absolute',
                            pointerEvents: 'none',
                            zIndex: 999,
                            ...(guide.type === 'horizontal'
                                ? { left: 0, right: 0, top: guide.position, height: 1 }
                                : { top: 0, bottom: 0, left: guide.position, width: 1 }
                            ),
                        }}
                    />
                ))}
                {containerWidth > 0 && dashJson.map((item, index) => {
                    const pos = toPixels(item);
                    const isBeingEdited = editingItem?.index === index;

                    return (
                        <Rnd
                            key={`${dashPageId}-${index}-${cardBumpKeys[index] || 0}-${undoRedoKey}-${stableColWidth}`}
                            default={{ x: pos.x, y: pos.y, width: pos.width, height: pos.height }}
                            minWidth={colWidth}
                            minHeight={ROW_HEIGHT - 20}
                            bounds="parent"
                            disableDragging={!editMode}
                            enableResizing={editMode
                                ? { top: true, bottom: true, left: true, right: true, topLeft: true, topRight: true, bottomLeft: true, bottomRight: true }
                                : false
                            }
                            className={`dash-rnd-item ${editMode ? 'edit-mode' : ''} ${isBeingEdited ? 'is-editing' : ''}`}
                            dragHandleClassName="dash-drag-handle"
                            onDrag={(e, d) => {
                                const current = cardRectsRef.current[index];
                                const w = current ? current.width  : dashJson[index].width  * colWidth;
                                const h = current ? current.height : dashJson[index].height * ROW_HEIGHT - 20;
                                const liveRect = { x: d.x, y: d.y, width: w, height: h };
                                updateGuides(index, liveRect, { showDanger: false, hideWhenColliding: true });

                                // When there's open space, this is a normal free-drag move.
                                // Hide insertion line so we don't imply a forced reorder.
                                if (!hasCollisionPx(index, liveRect)) {
                                    setInsertionLine(null);
                                    return;
                                }

                                // Compute insertion-line position (vertical or horizontal)
                                const { targetIdx, insertAfter, isHorizontal } = getInsertionPoint(index, liveRect);
                                if (targetIdx >= 0) {
                                    const r = cardRectsRef.current[targetIdx];
                                    if (r) {
                                        if (isHorizontal) {
                                            // Drag is top/bottom dominant → horizontal line
                                            const lineY = insertAfter
                                                ? r.y + r.height - 2   // bottom edge
                                                : r.y            - 2;  // top edge
                                            setInsertionLine({
                                                x:          r.x - 6,
                                                y:          lineY,
                                                width:      r.width + 12,
                                                height:     4,
                                                horizontal: true,
                                            });
                                        } else {
                                            // Drag is left/right dominant → vertical line
                                            const lineX = insertAfter
                                                ? r.x + r.width  - 2   // right edge
                                                : r.x            - 2;  // left edge
                                            setInsertionLine({
                                                x:          lineX,
                                                y:          r.y - 6,
                                                width:      4,
                                                height:     r.height + 12,
                                                horizontal: false,
                                            });
                                        }
                                    }
                                } else {
                                    setInsertionLine(null);
                                }
                            }}
                            onDragStop={(e, d) => handleDragStop(index, d)}
                            onResize={(e, dir, ref, delta, position) => {
                                updateGuides(
                                    index,
                                    { x: position.x, y: position.y, width: ref.offsetWidth, height: ref.offsetHeight },
                                    { showDanger: true, hideWhenColliding: false }
                                );
                            }}
                            onResizeStop={(e, dir, ref, delta, position) => handleResizeStop(index, ref, position)}
                            resizeHandleStyles={{
                                right:       { width: '8px',  right: '-4px',  top: '20%',    height: '60%',  cursor: 'ew-resize',   zIndex: 200 },
                                left:        { width: '8px',  left: '-4px',   top: '20%',    height: '60%',  cursor: 'ew-resize',   zIndex: 200 },
                                bottom:      { height: '8px', bottom: '-4px', left: '20%',   width: '60%',   cursor: 'ns-resize',   zIndex: 200 },
                                top:         { height: '8px', top: '-4px',    left: '20%',   width: '60%',   cursor: 'ns-resize',   zIndex: 200 },
                                bottomRight: { width: '20px', height: '20px', bottom: '-4px', right: '-4px', cursor: 'nwse-resize', zIndex: 201 },
                                bottomLeft:  { width: '20px', height: '20px', bottom: '-4px', left: '-4px',  cursor: 'nesw-resize', zIndex: 201 },
                                topRight:    { width: '20px', height: '20px', top: '-4px',    right: '-4px', cursor: 'nesw-resize', zIndex: 201 },
                                topLeft:     { width: '20px', height: '20px', top: '-4px',    left: '-4px',  cursor: 'nwse-resize', zIndex: 201 },
                            }}
                            resizeHandleClasses={{
                                right:       'rnd-handle rnd-handle-e',
                                left:        'rnd-handle rnd-handle-w',
                                bottom:      'rnd-handle rnd-handle-s',
                                top:         'rnd-handle rnd-handle-n',
                                bottomRight: 'rnd-handle rnd-handle-se',
                                bottomLeft:  'rnd-handle rnd-handle-sw',
                                topRight:    'rnd-handle rnd-handle-ne',
                                topLeft:     'rnd-handle rnd-handle-nw',
                            }}
                        >
                            {/* Edit mode chrome - drag handle sits above the inset content */}
                            {editMode && (
                                <div className="dash-drag-handle" style={{ position: 'absolute', top: 4, left: 4, right: 4, zIndex: 10 }}>
                                    <span className="drag-dots">
                                        <Icon name="ellipsis horizontal" />
                                        <Icon name="ellipsis horizontal" />
                                    </span>
                                    <span className="drag-label">{item.title || 'Untitled Widget'}</span>
                                    <span className="drag-actions">
                                        <button
                                            className={`drag-action-btn ${isBeingEdited ? 'active' : ''}`}
                                            onClick={(e) => { e.stopPropagation(); setEditingItem(isBeingEdited ? null : { item, index }); }}
                                            title="Configure widget"
                                        >
                                            <Icon name="cog" />
                                        </button>
                                        <button
                                            className="drag-action-btn delete"
                                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmIndex(index); }}
                                            title="Delete widget"
                                        >
                                            <Icon name="trash alternate" />
                                        </button>
                                    </span>
                                </div>
                            )}

                            {/* Card content — inset by CARD_GAP in view mode to create visual spacing */}
                            <div
                                className="dash-rnd-content"
                                style={{
                                    position: 'absolute',
                                    top:    editMode ? 40 : 4,
                                    left:   4,
                                    right:  4,
                                    bottom: 4,
                                    overflow: 'hidden',
                                    borderRadius: editMode ? '0 0 10px 10px' : '14px',
                                }}
                            >
                                <div className="dash-grid-cell" style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                                    {renderCardContent(item, index)}
                                </div>
                            </div>

                            {/* Delete confirmation inline overlay */}
                            {deleteConfirmIndex === index && (
                                <div className="dash-delete-confirm-overlay">
                                    <p>Delete this widget?</p>
                                    <button className="confirm-yes" onClick={() => handleDeleteItem(index)}>Delete</button>
                                    <button className="confirm-no" onClick={() => setDeleteConfirmIndex(null)}>Cancel</button>
                                </div>
                            )}
                        </Rnd>
                    );
                })}
            </div>
            {isEditPanelOpen && editingItem && createPortal((() => {
                const { top, bottom } = getPanelBounds();
                const side = getPanelSide();
                return (
                <div
                    className={`dash-edit-panel open panel-${side}`}
                    style={{
                        top,
                        bottom,
                        // Round only the corners that face inward so it looks
                        // like the panel is attached to the viewport edge
                        borderRadius: side === 'left'
                            ? '0 12px 12px 0'
                            : '12px 0 0 12px',
                    }}
                >
                    {/* Header */}
                    <StyledContainer
                        className="dash-edit-panel-header"
                        bgColor={CorvalUIColor.LIGHT_SCREEN}
                        borderSize={[null, null, Size.SM, null]}
                        borderColor={CorvalUIColor.DK_SCREEN_25}
                        padding={[Size.SM, Size.MD]}
                    >
                        <Flexbox justify={FlexJustify.SPACE_BETWEEN} align={FlexAlign.CENTER}>
                            <FlexItem>
                                <div className="dash-edit-panel-title-group">
                                    <span className="dash-edit-panel-title">Configure Widget</span>
                                    <span className="dash-edit-panel-subtitle">
                                        {editingItem.item.title || 'Untitled Widget'}
                                    </span>
                                </div>
                            </FlexItem>
                            <FlexItem noGrow>
                                <Button
                                    icon="times"
                                    basic
                                    size="mini"
                                    onClick={() => setEditingItem(null)}
                                    className="dash-edit-panel-close"
                                />
                            </FlexItem>
                        </Flexbox>
                    </StyledContainer>

                    {/* Scrollable body */}
                    <div className="dash-edit-panel-body">
                        <BuildForm
                            procedures={procedures}
                            chartTypes={chartTypeOptions}
                            initialItem={editingItem.item}
                            onSubmit={handleItemFormSave}
                            onCloseForm={() => setEditingItem(null)}
                            standalone
                            formId="widget-config-form"
                            hideActions
                        />
                    </div>

                    {/* Sticky footer — always visible at the bottom */}
                    <StyledContainer
                        className="dash-edit-panel-footer"
                        bgColor={CorvalUIColor.LIGHT_SCREEN}
                        borderSize={[Size.SM, null, null, null]}
                        borderColor={CorvalUIColor.DK_SCREEN_25}
                        padding={[Size.SM, Size.MD]}
                    >
                        <Flexbox justify={FlexJustify.END} align={FlexAlign.CENTER}>
                            <FlexItem noGrow>
                                <Button
                                    basic
                                    size="small"
                                    onClick={() => setEditingItem(null)}
                                >
                                    Cancel
                                </Button>
                            </FlexItem>
                            <FlexItem noGrow>
                                <Button
                                    type="submit"
                                    form="widget-config-form"
                                    size="small"
                                    className="save-btn"
                                    icon="check"
                                    content="Save"
                                />
                            </FlexItem>
                        </Flexbox>
                    </StyledContainer>
                </div>
                );
            })(), document.body
            )}
        </div>
    );
};

export default DashGrid;
